import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAllConversars,
  deleteConversar,
  archiveConversar,
  restoreConversar,
  bulkArchiveConversar,
  bulkRestoreConversar,
  bulkDeleteConversar,
} from '../../redux/actions/conversar';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import Loader from '../../../../components/Loader';
import EnhancedTable from '../../components/Table';
import PageHeader from '../../components/PageHeader';
import DeleteDialog from 'components/DeleteDialog';

import { IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';
import ArchiveIcon from '@material-ui/icons/Archive';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import FilterBar from '../../components/FilterBar';
import SettingDialog from 'containers/SuperAdmin/components/SettingDialog';
const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
}));

export default function Conversar({ name, id }) {
  const [view, setView] = useState('normal');
  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [searchString, setSearchString] = useState('');
  const { conversars, fetching } = useSelector(({ admin }) => admin.conversar);
  const [sort, setSort] = useState({ orderBy: '_id', orderDir: 'desc' });

  const loadPage = (page, sortOptions = {}) => {
    if (Object.values(sortOptions).length === 0) {
      sortOptions = sort;
    }
    let update = { page, limit: conversars.limit, ...sortOptions };
    if (selectedTopics) {
      update = { ...update, topics: selectedTopics.toString() };
    }
    if (searchString !== '') {
      update = { ...update, search: searchString };
    }
    if (view === 'archived') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllConversars(update));
    setSort(sortOptions);
  };

  const headCells = [
    {
      id: 'checkbox',
      label: 'Select',
    },
    { id: 'name', label: 'Name' },
    {
      id: 'actions',
      label: 'Actions',
      actions:
        view === 'normal'
          ? [
              {
                label: 'Edit',
                action: (r) => {
                  history.push(`/superadmin/conversars/edit/${r._id}`);
                },
                icon: <EditIcon />,
                disabled: () => false,
              },
              {
                label: 'Archive',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <ArchiveIcon />,
                disabled: () => false,
              },
            ]
          : [
              {
                label: 'Restore',
                action: (r) => {
                  dispatch(restoreConversar(r._id));
                },
                icon: <RestoreFromTrashIcon />,
                disabled: () => false,
              },
              {
                label: 'Permanently Delete',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <DeleteForeverIcon />,
                disabled: () => false,
              },
            ],
    },
  ];

  useEffect(() => {
    dispatch(fetchAllConversars());
  }, [dispatch]);

  const handleSearch = (d) => {
    setSearchString(d);
    let update = {
      page: conversars.page,
      limit: conversars.limit,
      search: d,
      ...sort,
    };
    if (selectedTopics) {
      update = { ...update, topics: selectedTopics.toString() };
    }
    if (view === 'archived') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllConversars(update));
  };

  const handleAction = () => {
    let update = {
      page: conversars.page,
      limit: conversars.limit,
      topics: selectedTopics.toString(),
      ...sort,
    };
    if (searchString !== '') {
      update = { ...update, search: searchString };
    }
    if (view === 'normal') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllConversars(update));
    setView(view === 'normal' ? 'archived' : 'normal');
  };

  const handleTopic = (e) => {
    setSelectedTopics(e);
    let update = {
      page: conversars.page,
      limit: conversars.limit,
      topics: e.toString(),
      ...sort,
    };
    if (searchString !== '') {
      update = { ...update, search: searchString };
    }
    if (view === 'archived') {
      update = { ...update, isArchived: true };
    }
    dispatch(fetchAllConversars(update));
  };

  return (
    <div className={classes.root}>
      {fetching && <Loader fetching={fetching} />}
      <PageHeader
        disableSearch
        title={name}
        options={
          <>
            <SettingDialog
              id={id}
              name={name}
              enableLabel={false}
              enableContentEditor
            />
            <IconButton
              onClick={() => {
                history.push('/superadmin/conversars/new');
              }}
            >
              <AddIcon />
            </IconButton>
          </>
        }
      />
      <FilterBar
        enableTopics={true}
        enableGroups={false}
        toggle={view === 'archived'}
        onSearch={handleSearch}
        topics={selectedTopics}
        onTopicSelect={handleTopic}
        onAction={handleAction}
      />
      {conversars && conversars.docs.length > 0 && (
        <Grid container direction='row' justifyContent='flex-start' spacing={2}>
          <Grid item sm={12} xs={12}>
            <EnhancedTable
              data={{
                ...conversars,
                docs: conversars.docs.filter((x) =>
                  view === 'normal' ? !x.isArchived : x.isArchived
                ),
              }}
              view={view}
              headCells={headCells}
              handlePage={loadPage}
              orderField={sort.orderBy}
              orderDirection={sort.orderDir}
              handleOrder={(d) => loadPage(conversars.page, d)}
              handleBulkDelete={(ids) => dispatch(bulkDeleteConversar(ids))}
              handleBulk={(ids) => {
                if (view === 'normal') {
                  dispatch(bulkArchiveConversar(ids));
                } else {
                  dispatch(bulkRestoreConversar(ids));
                }
              }}
            />
          </Grid>
        </Grid>
      )}
      <DeleteDialog
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        handleConfirm={() => {
          if (view === 'archived') {
            dispatch(deleteConversar(deleteId));
          } else {
            dispatch(archiveConversar(deleteId));
          }
          setDeleteId(null);
          setOpenDeleteDialog(false);
        }}
      />
    </div>
  );
}
